import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { getTireDimensionsAPI, getTireWidthsAPI, getTireHeightsAPI, getTireRadiusAPI } from "services/api/fleet/tires/api";
import { Dropdown } from "../Dropdown";
import { DropdownOption } from "../Dropdown/types";

interface TireDimensionDropdownsProps {
    width: number | null;
    height: number | null;
    radius: number | null;
    onChange: (field: string, value: number | null) => void;
    errors?: { width?: string; height?: string; radius?: string };
    direction?: "up" | "down";
    /** Kad je true, koristi kaskadne podatke iz kataloga. Kad je false, koristi statičke liste. */
    cascading?: boolean;
}

const TireDimensionDropdowns: React.FC<TireDimensionDropdownsProps> = (props) => {
    const { width, height, radius, onChange, errors, direction = "down", cascading = true } = props;
    const { t } = useTranslate();

    const [widthOptions, setWidthOptions] = useState<DropdownOption[]>([]);
    const [heightOptions, setHeightOptions] = useState<DropdownOption[]>([]);
    const [radiusOptions, setRadiusOptions] = useState<DropdownOption[]>([]);

    // Dohvati width opcije na mount
    useEffect(() => {
        fetchWidths();
    }, []);

    // Kad se width promijeni → dohvati heights
    useEffect(() => {
        if (width) {
            fetchHeights(width);
        } else {
            setHeightOptions([]);
            setRadiusOptions([]);
        }
    }, [width]);

    // Kad se height promijeni → dohvati radii
    useEffect(() => {
        if (width && height) {
            fetchRadii(width, height);
        } else {
            setRadiusOptions([]);
        }
    }, [width, height]);

    const fetchWidths = async () => {
        try {
            if (cascading) {
                const data = await getTireDimensionsAPI();
                setWidthOptions(data.map((v) => ({ value: v, text: String(v) })));
            } else {
                const data = await getTireWidthsAPI();
                setWidthOptions(data.map((v) => ({ value: v.id, text: v.name })));
            }
        } catch { /* */ }
    };

    const fetchHeights = async (w: number) => {
        try {
            if (cascading) {
                const data = await getTireDimensionsAPI(w);
                setHeightOptions(data.map((v) => ({ value: v, text: String(v) })));
            } else {
                const data = await getTireHeightsAPI();
                setHeightOptions(data.map((v) => ({ value: v.id, text: v.name })));
            }
        } catch { /* */ }
    };

    const fetchRadii = async (w: number, h: number) => {
        try {
            if (cascading) {
                const data = await getTireDimensionsAPI(w, h);
                setRadiusOptions(data.map((v) => ({ value: v, text: String(v) })));
            } else {
                const data = await getTireRadiusAPI();
                setRadiusOptions(data.map((v) => ({ value: v.id, text: v.name })));
            }
        } catch { /* */ }
    };

    const handleWidthChange = useCallback((_e: any, _name: string, item: DropdownOption) => {
        onChange("width", Number(item.value));
        onChange("height", null);
        onChange("radius", null);
    }, [onChange]);

    const handleHeightChange = useCallback((_e: any, _name: string, item: DropdownOption) => {
        onChange("height", Number(item.value));
        onChange("radius", null);
    }, [onChange]);

    const handleRadiusChange = useCallback((_e: any, _name: string, item: DropdownOption) => {
        onChange("radius", Number(item.value));
    }, [onChange]);

    return (
        <div className="flex gap-3">
            <div className="flex flex-1">
                <div className="flex-1">
                    <Dropdown
                        name="width"
                        label={t("admin.fleet.tire.add.width")}
                        placeholder={t("admin.fleet.tire.add.width.placeholder")}
                        options={widthOptions}
                        onChange={handleWidthChange}
                        value={width}
                        error={errors?.width}
                        direction={direction}
                        required
                        width="full"
                    />
                </div>
            </div>
            <div className="flex flex-1">
                <div className="flex-1">
                    <Dropdown
                        name="height"
                        label={t("admin.fleet.tire.add.height")}
                        placeholder={t("admin.fleet.tire.add.height.placeholder")}
                        options={heightOptions}
                        onChange={handleHeightChange}
                        value={height}
                        error={errors?.height}
                        direction={direction}
                        required
                        width="full"
                        disabled={!width}
                    />
                </div>
            </div>
            <div className="flex flex-1">
                <div className="flex-1">
                    <Dropdown
                        name="radius"
                        label={t("admin.fleet.tire.add.radius")}
                        placeholder={t("admin.fleet.tire.add.radius.placeholder")}
                        options={radiusOptions}
                        onChange={handleRadiusChange}
                        value={radius}
                        error={errors?.radius}
                        direction={direction}
                        required
                        width="full"
                        disabled={!height}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(TireDimensionDropdowns);
